import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    createViewFileReview,
    createReviewTab,
    createEtlTab,
    SupportingFilesTab,
    SupportingFileUploadModal,
    SupportingFileEditModal,
    type TFileReviewDetails,
    type TReviewLineItem,
} from "@/modules/rev_apac/review/views/ViewFileReview";
import { routerPaths } from "@/modules/rev_apac/router";
import type { TAsyncState } from "@/store";
import { initialAsyncState } from "@/store";
import { computeIsViewingAsAdmin } from "@/store/auth/sliceAuth";
import { mapApiSupportingFileToStore } from "@/utils/mapApiSupportingFileToStore";
import { wrapApiError } from "@/utils/wrapApiError";
import type {
    TApiFileLineItem,
    TApiFileOperationAccepted,
    TApiFileOperationStatus,
    TApiFinancialFile,
    TApiLineItemQuery,
    TApiComparisonQuery,
} from "@/apis/proda_finance/types";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import { wireGLLines } from "./wireGLLines";
import type { TLineItemFacets } from "@/modules/rev_apac/review/views/ViewGLLines/lineItemQuery";
import { searchMasterPropertyCodes, type TPropertyCodeKind } from "./masterPropertyCodeSearch";
import type { TAutoPopulateField } from "./views/ViewFileReview/useAutoPopulate";
import { pollUntil } from "@/utils/pollUntil";
import {
    mapApiSubmissionContext,
    type TSubmissionContext,
} from "@/modules/rev_apac/review/views/ViewFileReview/submissionContext";
import {
    mapApiSubmissionHistory,
    type TSubmissionHistory,
} from "@/modules/rev_apac/review/views/ViewFileReview/submissionHistory";
import { hasSurface } from "@/modules/rev_apac/review/views/ViewFileReview/reviewSurfaces";

const FILE_REVIEW_PAGE_SIZE = 100;

/** Parse validation errors from a JSON string and strip error code prefixes. */
function parseValidationErrors(raw: string | null): string[] {
    if (!raw) return [];
    try {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed
                .filter((x): x is string => typeof x === "string")
                .map((e) => {
                    const match = e.match(/^[A-Z][A-Z0-9_]+:\s*(.+)$/);
                    return match?.[1] ?? e;
                });
        }
    } catch {
        // not JSON, treat as plain string
    }
    const match = raw.match(/^[A-Z][A-Z0-9_]+:\s*(.+)$/);
    return [match?.[1] ?? raw];
}

/**
 * Read a string field out of a background operation's untyped `result` bag.
 * `result` is `Record<string, unknown> | null` on the wire (its shape depends
 * on which operation produced it — see TApiFileOperationStatus), so callers
 * that need one specific field go through here rather than casting.
 */
function resultString(result: Record<string, unknown> | null, key: string): string | null {
    const value = result?.[key];
    return typeof value === "string" ? value : null;
}

/** Read a number field out of a background operation's untyped `result` bag. */
function resultNumber(result: Record<string, unknown> | null, key: string): number {
    const value = result?.[key];
    return typeof value === "number" ? value : 0;
}

/**
 * Coerce a split row's edited amount to something the API will accept.
 *
 * The field is a free-text input, so strip grouping separators and whitespace and
 * fall back to "0" for anything that is not a number — the backend then re-runs the
 * split rules, which will keep the row invalid until a real amount is entered.
 */
function normalizeAmountInput(raw: string | null | undefined): string {
    const cleaned = (raw ?? "").replace(/[\s,]/g, "");
    return cleaned !== "" && Number.isFinite(Number(cleaned)) ? cleaned : "0";
}

/**
 * Map an API line item to the review-grid shape.
 *
 * Applied recursively to `split_children`, which the list endpoint nests under
 * each top-level row (the endpoint itself returns top-level rows only). Splits
 * are one level deep, so a child's own `splitChildren` is always empty.
 */
function toReviewLineItem(item: TApiFileLineItem): TReviewLineItem {
    return {
        id: item.id,
        rowNumber: item.line_number,
        trannum: item.trannum?.toString() || null,
        isValid: item.validation_status === "valid",
        validationStatus: item.validation_status,
        sourceAccountNumber: item.account_code,
        sourceAccountName: item.account_name,
        breAccountNumber: item.mapped_account_code,
        breAccountName: item.mapped_account_name,
        propcode: item.mapped_property_code,
        localPropcode: item.property_code,
        asset: item.asset_code ?? null,
        mappedAsset: item.mapped_asset_code ?? null,
        investment: item.investment_code ?? null,
        mappedInvestment: item.mapped_investment_code ?? null,
        sourceSystem: item.source_system ?? null,
        amount: item.amount || "0",
        debit: item.debit,
        credit: item.credit,
        transactionDate: item.transaction_date,
        postMonth: item.posting_date,
        bookNumber: item.book_number,
        description: item.description,
        ref: item.reference,
        exchangeRate: item.exchange_rate,
        exchangeRateDate: item.exchange_rate_date,
        displayType: item.display_type,
        tranCurrencyId: item.tran_currency_id,
        localCurrency: item.tran_currency_id,
        localAmount: item.detail_tran_amount,
        validationErrors: parseValidationErrors(item.validation_errors),
        isGenerated: false,
        isRebalanceRow: item.is_rebalance_row,
        canBeDeleted: item.is_rebalance_row || item.parent_line_id !== null,
        isSplitChild: item.parent_line_id !== null,
        parentLineId: item.parent_line_id,
        splitChildren: (item.split_children ?? []).map(toReviewLineItem),
    };
}

/**
 * Map the whole-file API payload to the review page's store shape.
 *
 * `dealFileType` / `finalFileDestination` come from the deal, not the file, so
 * callers that already know them (a fresh `loadFile`, which fetches the deal
 * too) pass the freshly-fetched values; callers reacting to a line-item edit
 * (which cannot change the deal) pass through the currently-loaded ones
 * instead of re-fetching the deal on every edit.
 */
function mapApiFileToReviewDetails(
    file: TApiFinancialFile,
    dealFileType: string | null,
    finalFileDestination: string | null,
): TFileReviewDetails {
    // Contributors are the file's stored set (backend). The first
    // contributor is surfaced as the "finance lead" in the header.
    const contributors = (file.contributors ?? []).map((c) => ({
        id: c.id,
        name: c.display_name,
    }));
    const financeLeadName = contributors[0]?.name ?? null;

    return {
        id: file.id,
        dealId: file.deal_id,
        dealName: file.deal_name,
        dealType: dealFileType,
        dealFileType: dealFileType,
        accountManager: financeLeadName,
        contributors,
        canEditContributors: file.can_edit_contributors ?? false,
        originalFilename: file.original_filename,
        status: file.status,
        fileType: file.file_type,
        period: file.period,
        periodLabel: file.period_label,
        totalLineItems: file.total_line_items,
        totalDebit: file.total_debit,
        totalCredit: file.total_credit,
        balance: file.balance,
        validationErrors: file.validation_errors,
        validationWarnings: file.validation_warnings,
        rejectionReason: file.rejection_reason ?? null,
        rejectedById: file.rejected_by_id ?? null,
        rejectedByName: file.rejected_by_name ?? null,
        rejectedAt: file.rejected_at ?? null,
        createdAt: file.created_at,
        uploadedAt: file.uploaded_at || null,
        uploadedById: file.uploaded_by_id ?? null,
        isLocked: file.is_locked ?? false,
        lockedAt: file.locked_at ?? null,
        lockedById: file.locked_by_id ?? null,
        lockReason: file.lock_reason ?? null,
        completedFromStatus: file.completed_from_status ?? null,
        archivedAt: file.archived_at ?? null,
        isSplitCompleted: file.is_split_completed ?? true,
        canSplitLineItems: file.can_split_line_items ?? false,
        parseConfigChanged: file.parse_config_changed ?? false,
        manualEditCount: file.manual_edit_count ?? 0,
        splitLineCount: file.split_line_count ?? 0,
        invalidLineItemCount: file.invalid_line_item_count ?? 0,
        allLineItemsValid: file.all_line_items_valid ?? false,
        hasRebalanceRows: file.has_rebalance_rows ?? false,
        hasGLGeneratedLines:
            file.status === "balanced" ||
            file.status === "adjusted" ||
            file.status === "committed" ||
            file.status === "data_platform_generated" ||
            file.status === "finjournal_generated" ||
            file.status === "completed",
        finalFileDestination: finalFileDestination,
    };
}

export const wireFileReview = ({ store, useAppStore, api }: TCompositionDeps) => {
    const ReviewTab = createReviewTab();
    const EtlTab = createEtlTab();
    const { GeneratedGLTab } = wireGLLines({ store, useAppStore, api });
    const ViewFileReview = createViewFileReview({
        ReviewTab,
        EtlTab,
        GeneratedGLTab,
        SupportingFilesTab,
        SupportingFileUploadModal,
        SupportingFileEditModal,
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const navigate = useNavigate();

            // Server-derived record of the current file's background operation
            // (at most one runs at a time — the backend enforces this with a file
            // lock), so the buttons can gate on something that survives a reload
            // or a navigation away and back, not just this session's local state.
            const fileOperationStatus = useAppStore((state) => state.fileOperationStatus);
            const setFileOperationStatus = useAppStore((state) => state.setFileOperationStatus);
            const clearFileOperationStatus = useAppStore((state) => state.clearFileOperationStatus);

            const [fileState, setFileState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                data: TFileReviewDetails | null;
                error: Error | null;
            }>({ status: "idle", data: null, error: null });

            const [submissionContextState, setSubmissionContextState] =
                useState<TAsyncState<TSubmissionContext>>(initialAsyncState<TSubmissionContext>());

            // Lazily loaded: only the Submission history view needs it, and most
            // visits to the page never open it.
            const [submissionHistoryState, setSubmissionHistoryState] =
                useState<TAsyncState<TSubmissionHistory>>(initialAsyncState<TSubmissionHistory>());
            // Monotonic id per submission-context request. The fileId guard alone cannot
            // separate two months of the *same* file, so a slow first month could land
            // after a fast second one and leave the header describing a month nobody asked for.
            const contextRequestRef = useRef(0);
            // The same guard for history, for the same reason: a month switch issues a
            // second request for the same file, which the fileId check cannot tell apart.
            const historyRequestRef = useRef(0);

            const [lineItemsState, setLineItemsState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                data: TReviewLineItem[] | null;
                error: Error | null;
            }>({ status: "idle", data: null, error: null });

            const [lineItemsMeta, setLineItemsMeta] = useState<{
                total: number;
                totalUnfiltered: number;
                pageSize: number;
            }>({ total: 0, totalUnfiltered: 0, pageSize: FILE_REVIEW_PAGE_SIZE });

            const [facets, setFacets] = useState<TLineItemFacets | null>(null);
            // Filter query the facets are scoped to, the file+query key they were last
            // loaded for (dedupe), and the in-flight request (superseded on change).
            const facetQueryRef = useRef<TApiLineItemQuery>({});
            const loadedFacetKeyRef = useRef<string | null>(null);
            const facetAbortRef = useRef<AbortController | null>(null);
            const lastLineQueryRef = useRef<TApiLineItemQuery>({
                skip: 0,
                limit: FILE_REVIEW_PAGE_SIZE,
            });

            const currentFileIdRef = useRef<string | null>(null);

            // The file's currently loaded status — kept alongside currentFileIdRef so
            // onChangeStatus can tell a reopen apart from an ordinary status change
            // without reading a stale closure value.
            const currentStatusRef = useRef<string | null>(null);

            // Whether the last poll observed this file locked for background
            // revalidation — used to detect the lock being set/cleared.
            const wasRevalidatingRef = useRef(false);

            const loadFile = useCallback(async (fileId: string) => {
                currentFileIdRef.current = fileId;
                // Unconditionally keep whatever is already loaded while the fetch is
                // pending — this must NOT be derived from currentFileIdRef (e.g. "was it
                // null before this call"). The shell's effect cleanup runs onUnmount,
                // which nulls that ref, before the next mount's onMount -> loadFile runs,
                // so on a real month switch the ref is always null by the time we get
                // here; gating on it would blank fileState.data and trip the shell's
                // full-page loader on every switch, not just the first mount. Keeping
                // prev.data unconditionally is still correct on a genuine first load,
                // since prev.data is null there anyway — the full-page spinner still
                // shows exactly once. The content panel knows a switch is still in
                // flight because `data.id` does not yet match the file in the URL.
                setFileState((prev) => ({
                    status: "pending",
                    data: prev.data,
                    error: null,
                }));

                try {
                    const file = await api.files.get(fileId);

                    let dealFileType: string | null = null;
                    let finalFileDestination: string | null = null;
                    try {
                        const deal = await api.deals.get(file.deal_id);
                        dealFileType = deal.deal_file_type;
                        finalFileDestination = deal.final_file_destination;
                    } catch {
                        // If deal fetch fails (e.g. standalone FinJournal with no deal),
                        // leave dealFileType / finalFileDestination as their defaults.
                    }

                    // A response for a file the user has since switched away from must not
                    // overwrite the one now on screen — same guard `fetchLineItems` and
                    // `loadSubmissionContext` use for their own writes. Checked before
                    // `currentStatusRef` too: the polling logic reads that ref, so a stale
                    // write there would corrupt it for the file actually being viewed.
                    if (currentFileIdRef.current !== fileId) return;

                    currentStatusRef.current = file.status;

                    setFileState({
                        status: "fulfilled",
                        data: mapApiFileToReviewDetails(file, dealFileType, finalFileDestination),
                        error: null,
                    });
                } catch (err) {
                    // Same guard as the success path: a stale rejection for a file the user
                    // has left must not blank the one now on screen.
                    if (currentFileIdRef.current !== fileId) return;
                    setFileState({
                        status: "rejected",
                        data: null,
                        error: err instanceof Error ? err : new Error("Failed to load file"),
                    });
                }
            }, []);

            // The submission context is what makes the v2 shell's header, month picker and
            // review-mode selectors possible. It is deliberately independent of `loadFile`:
            // a failed context must still leave a usable review page, so its error is held
            // here rather than propagated into `fileState`.
            const loadSubmissionContext = useCallback(
                async (fileId: string, period?: string) => {
                    const requestId = ++contextRequestRef.current;
                    // Keep any already-loaded context visible while refetching, so a month
                    // switch does not blank the header. Every field is named because
                    // TAsyncState is a union — see the note above.
                    setSubmissionContextState((prev) => ({
                        status: "pending",
                        data: prev.data,
                        error: null,
                    }));
                    try {
                        const response = await api.files.submissionContext(
                            fileId,
                            // Callers speak `YYYY-MM`; the endpoint takes any date inside
                            // the month and normalizes it to the first.
                            period ? { period: `${period}-01` } : undefined,
                        );
                        // A slower request for a file — or a month — the user has already
                        // moved on from must not overwrite the header. Same guard
                        // `isStillViewing` uses for its own writes.
                        if (
                            currentFileIdRef.current !== fileId ||
                            contextRequestRef.current !== requestId
                        ) {
                            return;
                        }
                        setSubmissionContextState({
                            status: "fulfilled",
                            data: mapApiSubmissionContext(response),
                            error: null,
                        });
                    } catch (error) {
                        // Guarded for the same reason: a stale rejection would blank a
                        // header that is correctly describing the file now on screen.
                        if (
                            currentFileIdRef.current !== fileId ||
                            contextRequestRef.current !== requestId
                        ) {
                            return;
                        }
                        setSubmissionContextState({
                            status: "rejected",
                            data: null,
                            error:
                                error instanceof Error
                                    ? error
                                    : new Error("Failed to load submission"),
                        });
                    }
                },
                [api.files],
            );

            const loadSubmissionHistory = useCallback(
                async (fileId: string, period?: string) => {
                    const requestId = ++historyRequestRef.current;
                    // Keep the previous rows visible while refetching so switching month
                    // does not blank the table. Every field is named because TAsyncState
                    // is a union. Rows belonging to a *different* file are not kept —
                    // onUnmount clears them before the next file mounts.
                    setSubmissionHistoryState((prev) => ({
                        status: "pending",
                        data: prev.data,
                        error: null,
                    }));
                    try {
                        const response = await api.files.submissionHistory(
                            fileId,
                            // Callers speak `YYYY-MM`, as they do for the context; the
                            // endpoint takes any date inside the month and normalizes it
                            // to the first.
                            period ? { period: `${period}-01` } : undefined,
                        );
                        // A slower request for a file — or a month — the user has already
                        // moved on from must not overwrite the table with another
                        // submission's attempts. The request id is what separates two
                        // months of the *same* file, which the fileId check cannot.
                        if (
                            currentFileIdRef.current !== fileId ||
                            historyRequestRef.current !== requestId
                        ) {
                            return;
                        }
                        setSubmissionHistoryState({
                            status: "fulfilled",
                            data: mapApiSubmissionHistory(response),
                            error: null,
                        });
                    } catch (error) {
                        if (
                            currentFileIdRef.current !== fileId ||
                            historyRequestRef.current !== requestId
                        ) {
                            return;
                        }
                        setSubmissionHistoryState({
                            status: "rejected",
                            data: null,
                            error:
                                error instanceof Error
                                    ? error
                                    : new Error("Failed to load submission history"),
                        });
                    }
                },
                [api.files],
            );

            const fetchLineItems = useCallback(
                async (fileId: string, query: TApiLineItemQuery, signal?: AbortSignal) => {
                    const merged: TApiLineItemQuery = {
                        ...query,
                        exclude_gl_generated: true,
                        limit: FILE_REVIEW_PAGE_SIZE,
                    };
                    lastLineQueryRef.current = merged;
                    setLineItemsState((prev) => ({
                        status: "pending",
                        data: prev.data,
                        error: null,
                    }));
                    try {
                        const response = await api.files.getLineItems(fileId, merged, signal);
                        // Only `useServerTable` passes a signal to abort a superseded
                        // request; refresh/after-split/revalidation callers do not, so a
                        // response for a file the user has since switched away from can
                        // still land here. Guarded the same way `loadSubmissionContext`
                        // guards its own writes: without this, a late response would
                        // repaint the previous file's rows and totals under the new
                        // file's header until the fresh fetch for it returns.
                        if (currentFileIdRef.current !== fileId) return;
                        setLineItemsMeta({
                            total: response.total,
                            totalUnfiltered: response.total_unfiltered ?? response.total,
                            pageSize: response.page_size,
                        });
                        setLineItemsState({
                            status: "fulfilled",
                            data: response.items.map(toReviewLineItem),
                            error: null,
                        });
                    } catch (err) {
                        // Swallow abort errors — the superseding request owns the loading state.
                        if (err instanceof DOMException && err.name === "AbortError") return;
                        // Same guard as the success path: a stale rejection for a file the
                        // user has left must not blank the one now on screen.
                        if (currentFileIdRef.current !== fileId) return;
                        setLineItemsState({
                            status: "rejected",
                            data: null,
                            error:
                                err instanceof Error ? err : new Error("Failed to load line items"),
                        });
                    }
                },
                [],
            );

            // Facets are scoped to the active column filters (the server narrows each
            // list by the other filters in the group), so the last filter query is
            // remembered: callers that only want fresh values after a mutation pass
            // `force` and keep the filters the reviewer has applied.
            const loadFacets = useCallback(
                async (
                    fileId: string,
                    query?: TApiLineItemQuery,
                    options?: { force?: boolean },
                ) => {
                    if (query !== undefined) facetQueryRef.current = query;
                    const key = `${fileId}|${JSON.stringify(facetQueryRef.current)}`;
                    // Same file and same filters as the loaded facets → nothing to do.
                    // Mount and filter-change both route here, so this is what keeps a
                    // page load from paying for the (16-query) facet request twice.
                    if (!options?.force && key === loadedFacetKeyRef.current) return;
                    loadedFacetKeyRef.current = key;
                    facetAbortRef.current?.abort();
                    const controller = new AbortController();
                    facetAbortRef.current = controller;
                    try {
                        const next = await api.files.getLineItemFilterOptions(
                            fileId,
                            { excludeGLGenerated: true },
                            facetQueryRef.current,
                            controller.signal,
                        );
                        if (controller.signal.aborted) return;
                        setFacets(next);
                    } catch (err) {
                        // Superseded request — the newer one owns the facet state.
                        if (err instanceof DOMException && err.name === "AbortError") return;
                        // Allow a retry of the same query after a failure.
                        loadedFacetKeyRef.current = null;
                        setFacets(null);
                    }
                },
                [],
            );

            // Refetch only the current page (current filters/sort/page) after a
            // mutation, instead of reloading every line item.
            const refetchCurrentPage = useCallback(async () => {
                const fileId = currentFileIdRef.current;
                if (!fileId) return;
                await fetchLineItems(fileId, lastLineQueryRef.current);
            }, [fetchLineItems]);

            // Whether `fileId` is still the file on screen. Every reload, navigation
            // or store write that happens *after* awaiting a background operation has
            // to check this: an operation can run for minutes, and `loadFile`
            // overwrites both `currentFileIdRef` and the displayed file, so acting on
            // a file the user has since navigated away from silently drags them back
            // to it.
            const isStillViewing = useCallback(
                (fileId: string) => currentFileIdRef.current === fileId,
                [],
            );

            // A poll that throws must not leave the record saying "running". Nothing
            // else clears it — only `onUnmount` does — and `isFileOperationRunning`
            // gates the grid and every whole-file action on it, so one transient
            // operation-status failure would otherwise keep the page disabled until
            // the user remounted it. Dropping the record falls back to "no operation
            // known", the same fallback `seedOperationState`'s catch documents: the
            // server's file lock still rejects a second start with a 409, so
            // re-enabling the buttons cannot corrupt anything, and the next mount
            // re-seeds the truth from the server.
            //
            // Guarded on the current file for the usual reason: if the user has since
            // navigated away, the record on the store belongs to the file they are
            // looking at now and clearing it would wrongly re-enable that one.
            const clearStaleRunningStatus = useCallback(
                (fileId: string) => {
                    if (isStillViewing(fileId)) {
                        clearFileOperationStatus();
                    }
                },
                [clearFileOperationStatus, isStillViewing],
            );

            // Poll operation-status until it leaves "running", writing every tick
            // (not just the final value) into the store so a component reading
            // `fileOperationStatus` sees live progress, not just start/finish.
            // Guarded by current-file identity: this poll can keep running for a
            // file the user has since navigated away from (nothing cancels it —
            // see the reload guard in `seedOperationState` below for the same
            // reason), and the store holds only one record. Without this guard, a
            // tick for file A would clobber file B's own live "running" record
            // while the user is viewing B, re-enabling B's buttons mid-operation.
            // Owns one invariant for both callers: this poll never returns leaving the
            // store saying "running". A record stuck that way disables the grid and
            // every whole-file action for good, because nothing re-polls afterwards and
            // only `onUnmount` clears it.
            const pollOperationStatus = useCallback(
                async (fileId: string): Promise<TApiFileOperationStatus> => {
                    try {
                        const final = await pollUntil(
                            async () => {
                                const status = await api.files.operationStatus(fileId);
                                if (currentFileIdRef.current === fileId) {
                                    setFileOperationStatus({
                                        fileId,
                                        operation: status.operation,
                                        state: status.state,
                                        error: status.error,
                                    });
                                }
                                return status;
                            },
                            (status) => status.state !== "running",
                        );
                        // `pollUntil` hands back its last value when the attempt budget
                        // runs out, so a still-"running" state here means this client
                        // gave up — not that the run ended. That is reachable without
                        // anything going wrong: `claim` re-stamps `started_at` so the
                        // server's 15-minute stale window measures the run rather than
                        // the queue wait, so a queued-then-claimed run can outlive this
                        // poll's ~17-minute budget while the server still considers it
                        // healthy. Drop the record instead of leaving it to gate the
                        // page; the file lock still 409s a second start, and the next
                        // mount re-seeds the truth.
                        if (final.state === "running") {
                            clearStaleRunningStatus(fileId);
                        }
                        return final;
                    } catch (err) {
                        // Same reasoning for a transient operation-status failure: the
                        // "running" written by an earlier tick must not outlive it.
                        clearStaleRunningStatus(fileId);
                        throw err;
                    }
                },
                [api.files, setFileOperationStatus, clearStaleRunningStatus],
            );

            // Every whole-file action (auto-balance, generate-gl, run-adjustment,
            // remove-generated-lines, reprocess, revalidate) now returns a 202
            // acknowledgement and runs on the Celery worker, so the lifecycle is
            // the same for all six: start it, poll operation-status until it
            // leaves "running", and throw on anything other than "succeeded" so
            // the caller's own try/catch (already wired per action button in the
            // view) surfaces the failure instead of proceeding to reload
            // pre-operation state. The file lock backs this up server-side, but
            // this is what actually sequences same-file operations from the
            // client rather than relying on a 409.
            const runOperation = useCallback(
                async (
                    fileId: string,
                    start: () => Promise<TApiFileOperationAccepted>,
                ): Promise<TApiFileOperationStatus> => {
                    const accepted = await start();
                    // Reflect "running" immediately, before the first poll round
                    // trip resolves, so the buttons disable without a visible delay.
                    // Guarded the same way as `pollOperationStatus`'s own writes: the
                    // user may have navigated to a different file while `start()`
                    // was in flight, and this must not clobber that file's record.
                    if (currentFileIdRef.current === fileId) {
                        setFileOperationStatus({
                            fileId,
                            operation: accepted.operation,
                            state: "running",
                            error: null,
                        });
                    }
                    // Whether this ends by throwing, by exhausting the poll budget or
                    // with a terminal state, `pollOperationStatus` guarantees it does
                    // not leave the store claiming the run is still going.
                    const final = await pollOperationStatus(fileId);
                    if (final.state !== "succeeded") {
                        throw new Error(final.error ?? "The operation did not complete.");
                    }
                    return final;
                },
                [pollOperationStatus, setFileOperationStatus],
            );

            // Seed the operation record from the server on every file load —
            // including a page reload or a navigate-away-and-back — rather than
            // relying only on this session's own `runOperation` calls. `reprocess`
            // and `revalidate` can legitimately run for many minutes; without this,
            // reloading mid-operation would show every button as enabled again,
            // and a second click would just 409 against the file lock instead of
            // the user seeing why the action is unavailable. If the seeded status
            // is still "running", keep polling to the same completion/reload an
            // in-session call would have done, instead of leaving the buttons
            // disabled until the user does something else.
            const seedOperationState = useCallback(
                async (fileId: string) => {
                    try {
                        const status = await api.files.operationStatus(fileId);
                        // Same guard as `pollOperationStatus`/`runOperation`: this
                        // fetch can resolve after the user has already moved to a
                        // different file, and must not clobber that file's record.
                        if (currentFileIdRef.current === fileId) {
                            setFileOperationStatus({
                                fileId,
                                operation: status.operation,
                                state: status.state,
                                error: status.error,
                            });
                        }
                        if (status.state !== "running") return;
                        // The terminal status itself is already in the store (every
                        // poll tick writes it), so the outcome is only awaited here,
                        // not branched on.
                        await pollOperationStatus(fileId);
                        // The resumed poll can resolve long after this call started —
                        // reprocess/revalidate can run for many minutes — so the user
                        // may have since navigated to a different file. `loadFile`
                        // unconditionally overwrites `currentFileIdRef` and the
                        // displayed file, so reloading for a file that is no longer
                        // the one being viewed would silently swap the page back to
                        // it. Bail out instead of reloading anything in that case —
                        // the same idiom the revalidation-lock poller above uses.
                        //
                        // Reloads on ANY terminal state, not just "succeeded": the
                        // server releases the lock when a run fails too
                        // (FileOperationService.fail -> unlock), and the file snapshot
                        // this page loaded was taken while that lock was still held.
                        // Skipping the reload on failure left `isLocked` true for good
                        // — `actionsDisabled` gates every action and per-row edit on
                        // it, and the revalidation poller below only reacts to a
                        // "Revalidating" lock transition, so the other five operations
                        // had no path back short of a remount. A failed run can also
                        // have committed row changes before it broke (reprocess
                        // deletes rows before re-parsing), so the rows and facets are
                        // worth re-reading on the way through as well.
                        if (isStillViewing(fileId)) {
                            await loadFile(fileId);
                            await refetchCurrentPage();
                            await loadFacets(fileId, undefined, { force: true });
                        }
                    } catch {
                        // Non-fatal: the buttons simply behave as if no operation
                        // is known, matching the page's behavior before this
                        // endpoint's status was tracked at all. That has to be made
                        // true of the store as well — a resumed poll that throws
                        // after the seed already recorded "running" would otherwise
                        // leave the page disabled for good.
                        clearStaleRunningStatus(fileId);
                    }
                },
                [
                    api.files,
                    setFileOperationStatus,
                    pollOperationStatus,
                    loadFile,
                    refetchCurrentPage,
                    loadFacets,
                    clearStaleRunningStatus,
                    isStillViewing,
                ],
            );

            // Reload the rows after a split/reversal/split-row delete. The file
            // object carries isSplitCompleted and the invalid-row aggregates that
            // gate the actions outside the split area, so it has to be refreshed
            // alongside the rows too. The endpoints already revalidate, so no extra
            // revalidate call here.
            const afterSplitChange = useCallback(
                async (fileId: string) => {
                    await loadFile(fileId);
                    await refetchCurrentPage();
                },
                [loadFile, refetchCurrentPage],
            );

            // Split/reverse-split (unlike delete, below) return the whole
            // file-detail payload inline (FIN-561), the same one-round-trip shape
            // onEditLineItem consumes -- so apply it directly instead of paying a
            // second GET /files/{id} via afterSplitChange. The grid still needs
            // refetchCurrentPage() for the row-level split/delete effects.
            const applyFileFromResponse = useCallback((apiFile: TApiFinancialFile) => {
                currentStatusRef.current = apiFile.status;
                setFileState((prev) => ({
                    status: "fulfilled",
                    data: mapApiFileToReviewDetails(
                        apiFile,
                        prev.data?.dealFileType ?? null,
                        prev.data?.finalFileDestination ?? null,
                    ),
                    error: null,
                }));
            }, []);

            // Generic reload used after unrelated actions (OTB commit, adjustment,
            // remove-generated-lines, auto-populate). It revalidates WITHOUT
            // re-applying account mappings so manual BRE account overrides survive,
            // and swallows revalidation failure since these callers are
            // fire-and-forget — but it still awaits the operation to completion
            // (success or failure) before reloading, otherwise the reload races
            // the background run and shows pre-revalidation state.
            const handleRefresh = useCallback(async () => {
                const fileId = currentFileIdRef.current;
                if (!fileId) return;
                try {
                    await runOperation(fileId, () => api.files.revalidate(fileId));
                } catch {
                    // Ignore: still reload below to reflect the latest state.
                } finally {
                    // Only if this is still the file on screen — see `isStillViewing`.
                    if (isStillViewing(fileId)) {
                        await loadFile(fileId);
                        await refetchCurrentPage();
                        // Refresh filter facets too: auto-populate/edit can introduce
                        // new propcode/account values that must appear as filter options.
                        await loadFacets(fileId, undefined, { force: true });
                    }
                }
            }, [runOperation, loadFile, refetchCurrentPage, loadFacets, isStillViewing]);

            // Explicit Refresh button. Two strategies, picked by the server's
            // parse_config_changed flag:
            //
            //  - config unchanged → revalidate: re-applies account mappings from the
            //    current BP CoA and re-runs validation over the existing line items,
            //    preserving manual edits and splits.
            //  - config changed → reprocess: re-reads the source file so the deal's
            //    row-filtering rules and column mappings actually take effect. Rows
            //    only exist as line items after a parse, so re-validation could never
            //    add or remove them. This rebuilds every non-generated line item, which
            //    is why the view confirms first when there is manual work to lose.
            //
            // reprocess already re-runs validation and field mapping, so it does not
            // need a revalidate afterwards. One operation, one poll — the flag
            // picks which single background operation to await. Errors propagate
            // (after the reload below runs) so the view can report them.
            const handleManualRefresh = useCallback(
                async (reparse = false) => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) return;
                    try {
                        if (reparse) {
                            await runOperation(fileId, () => api.files.reprocess(fileId));
                        } else {
                            await runOperation(fileId, () => api.files.revalidate(fileId, true));
                        }
                    } finally {
                        if (isStillViewing(fileId)) {
                            await loadFile(fileId);
                            await refetchCurrentPage();
                            await loadFacets(fileId, undefined, { force: true });
                        }
                    }
                },
                [runOperation, loadFile, refetchCurrentPage, loadFacets, isStillViewing],
            );

            useEffect(() => {
                const fileId = currentFileIdRef.current;
                if (fileId) {
                    loadFile(fileId);
                    // Company switch: same file+filters, but the values behind them
                    // can differ, so bypass the dedupe.
                    loadFacets(fileId, undefined, { force: true });
                    void refetchCurrentPage();
                }
            }, [effectiveCompanyId, loadFile, loadFacets, refetchCurrentPage]);

            // Live-poll the file's lock state so a background "Revalidating" lock
            // — and its clearing — is reflected without a manual refresh. Only the
            // visual sign relies on this; edit-blocking is server-enforced (any
            // edit on a locked file is rejected regardless of what the UI shows).
            // Polls only while mounted and the tab is visible; on a transition it
            // reloads the file, and when the lock clears it also refreshes the
            // current line-item page so updated validation results are shown.
            useEffect(() => {
                const REVALIDATION_POLL_INTERVAL_MS = 5000;
                let cancelled = false;
                const tick = async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) return;
                    if (typeof document !== "undefined" && document.hidden) return;
                    try {
                        const file = await api.files.get(fileId);
                        if (cancelled || currentFileIdRef.current !== fileId) return;
                        const nowRevalidating =
                            (file.is_locked ?? false) && file.lock_reason === "Revalidating";
                        if (nowRevalidating !== wasRevalidatingRef.current) {
                            const wasRevalidating = wasRevalidatingRef.current;
                            wasRevalidatingRef.current = nowRevalidating;
                            await loadFile(fileId);
                            if (wasRevalidating && !nowRevalidating) {
                                await refetchCurrentPage();
                            }
                        }
                    } catch {
                        // Transient poll failure — try again on the next tick.
                    }
                };
                const timer = setInterval(() => void tick(), REVALIDATION_POLL_INTERVAL_MS);
                return () => {
                    cancelled = true;
                    clearInterval(timer);
                };
            }, [loadFile, refetchCurrentPage, api.files]);

            const storeUser = useAppStore((state) => state.user);
            const effectiveSubgroup = useAppStore((state) => state.effectiveSubgroup);
            const isViewingAsAdmin = computeIsViewingAsAdmin(storeUser, effectiveSubgroup);
            const permissions = storeUser?.permissions ?? [];
            // canReview and canDownload come from the shared surface map, so the
            // page's structure is gated identically here and on the server. The
            // write verbs below keep their own actions — they are review
            // mechanics, not page structure.
            const finjournalReviewCaps = {
                canReview: hasSurface("review_tab", permissions, { isFinJournal: true }),
                canWrite: permissions.includes("finjournal_line_edit"),
                canDownload: hasSurface("file_download", permissions, { isFinJournal: true }),
                // Refresh (revalidate) is gated by finjournal_line_edit for FinJournal files.
                canRefresh: permissions.includes("finjournal_line_edit"),
                // Generate FinJournal is a write verb held only by Account Manager /
                // Accountant / Business Partner+ — mirrors the TB/GL Generate-GL gate.
                canGenerate: permissions.includes("finjournal_generate"),
                // View-all (Product Owner / Onboarding / Service Support) bypasses the
                // contributor-assignment lock — they can always act and (re)assign.
                canViewAll: permissions.includes("finjournal_files_view_all"),
            };
            const tbglReviewCaps = {
                canReview: hasSurface("review_tab", permissions),
                canWrite: permissions.includes("tbgl_line_edit"),
                canDownload: hasSurface("file_download", permissions),
                // Refresh (revalidate) has its own permission (tbgl_refresh): Account
                // Manager / Accountant hold it; Viewer keeps line-edit but not refresh.
                canRefresh: permissions.includes("tbgl_refresh"),
            };
            // tbgl_autobalance proxies the whole TB/GL write-verb set (autobalance /
            // autopopulate / generate / adjust / remove) — Account Manager / Accountant
            // hold them all; Viewer and admins hold none. Viewer keeps line-edit only.
            const canAutoBalance = permissions.includes("tbgl_autobalance");
            // Splitting has its own action rather than reusing tbgl_line_edit, which
            // Viewer also holds. Account Manager / Accountant only.
            const canSplitLines = permissions.includes("tbgl_line_split");
            // Product-Owner-only override: Manual Reject on any file in any status,
            // even when the ordinary review write gate blocks it (admins are read-only).
            const canForceReject = permissions.includes("file_force_reject");
            // Product-Owner-only: close an exported period (status -> completed) and
            // reopen it. Overrides the ordinary review write gate, which admins lack.
            const canMarkCompleted = permissions.includes("file_mark_completed");

            // Supporting files for the review page's Supporting Files tab — same
            // list/upload/update/delete/download surface as the old standalone
            // supporting-files use-case, scoped here to a single deal+period
            // rather than the whole company.
            const supportingFilesListState = useAppStore((state) => state.supportingFilesListState);

            const fetchSupportingFiles = useCallback(
                (query: Record<string, unknown>, signal?: AbortSignal) => {
                    store.getState().setSupportingFilesLoading();
                    api.supportingFiles
                        .list(query as Parameters<typeof api.supportingFiles.list>[0], signal)
                        .then((response) => {
                            store.getState().setSupportingFilesList({
                                items: response.items.map(mapApiSupportingFileToStore),
                                total: response.total,
                                totalUnfiltered: response.total_unfiltered ?? response.total,
                                page: response.page,
                                pageSize: response.page_size,
                            });
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            store
                                .getState()
                                .setSupportingFilesError(
                                    wrapApiError(error, "Failed to load supporting files"),
                                );
                        });
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            return {
                onMount: useCallback(
                    (fileId: string) => {
                        // `loadFile` owns `currentFileIdRef` — it sets it synchronously
                        // at the top of its own body, before its first `await`, so by
                        // the time this call returns control here the ref is already
                        // correct for `loadFacets`/
                        // `seedOperationState` below. Setting it here too used to make
                        // `loadFile`'s own previous-vs-current tracking unreachable.
                        loadFile(fileId);
                        // The submission context is deliberately absent here: it is loaded
                        // per *month*, and only the shell's `?period=` knows which month
                        // that is — including on a reload of a link that carries one.
                        // A freshly opened file starts with no filters applied; the
                        // view re-requests these whenever its filter group changes.
                        loadFacets(fileId, {});
                        // A page reload or a navigate-away-and-back is a fresh mount,
                        // so this is what re-discovers a still-running background
                        // operation and keeps the buttons disabled for it.
                        void seedOperationState(fileId);
                    },
                    [loadFile, loadFacets, seedOperationState],
                ),
                onMountRestricted: useCallback((fileId: string) => {
                    // The shell's own submission-context effect (keyed on `fileId` and
                    // `?period=`) is what actually fetches the context for every caller,
                    // restricted or not — this only establishes `currentFileIdRef` before
                    // that request's response lands, since `loadSubmissionContext` drops
                    // any response that arrives while the ref points elsewhere. Fetching
                    // it again here would race that effect's own request for nothing.
                    currentFileIdRef.current = fileId;
                }, []),
                onUnmount: useCallback(() => {
                    currentFileIdRef.current = null;
                    // Drop the previous file's operation record immediately rather
                    // than waiting for the next mount's seed fetch to overwrite it —
                    // closes the (harmless, but needless) window where it could
                    // otherwise briefly hold a different file's fileId/state.
                    clearFileOperationStatus();
                    // Clear the previous file's rows/meta too. fetchLineItems keeps
                    // prev.data while a request is pending (correct for the same file:
                    // pagination/filtering must not flash empty), but on a month switch
                    // this cleanup runs before the next mount, and if left alone the
                    // remounted ReviewTab's first render would still see the old
                    // month's rows and total — painting stale line items under the new
                    // file's header until the fresh fetch returns.
                    setLineItemsState({ status: "idle", data: null, error: null });
                    setLineItemsMeta({
                        total: 0,
                        totalUnfiltered: 0,
                        pageSize: FILE_REVIEW_PAGE_SIZE,
                    });
                    // And the previous file's upload attempts, for the same reason: the
                    // loader keeps prev.data while pending (right for a month switch on
                    // one file), so without this the history tab would show the file
                    // being left behind under the new file's header until the fetch
                    // returns — and those rows belong to a different submission.
                    setSubmissionHistoryState(initialAsyncState<TSubmissionHistory>());
                }, [clearFileOperationStatus]),
                loadFacets,
                fetchLineItems,
                useFileState: () => fileState as TAsyncState<TFileReviewDetails>,
                useSubmissionContext: () => submissionContextState,
                loadSubmissionContext,
                useSubmissionHistory: () => submissionHistoryState,
                loadSubmissionHistory,
                onDownloadErrorReport: useCallback(
                    async (errorReportFileId: string) => {
                        // Deliberately takes the id: the history table downloads reports
                        // for attempts other than the one currently open, so reading
                        // currentFileIdRef here would fetch the wrong file's errors.
                        await api.files.downloadFile(errorReportFileId, "errors");
                    },
                    [api.files],
                ),
                loadOriginalPreview: useCallback(
                    (previewFileId: string, sheet?: string) =>
                        api.files.originalPreview(previewFileId, { sheet }),
                    [],
                ),
                onDownloadOriginal: useCallback(
                    (downloadFileId: string) => api.files.downloadOriginal(downloadFileId),
                    [],
                ),
                useOperatingStatementEnabled: () =>
                    Boolean(storeUser?.companyFeatureFlags?.operating_statement_enabled),
                loadOperatingStatement: useCallback(
                    (statementFileId: string, propertyCode: string | undefined) =>
                        api.files.operatingStatement(statementFileId, { propertyCode }),
                    [],
                ),
                onDownloadOperatingStatement: useCallback(
                    (statementFileId: string, propertyCode: string | undefined) =>
                        api.files.downloadOperatingStatement(statementFileId, propertyCode),
                    [],
                ),
                // The permission surface AND the company flag (off by default everywhere). Gating
                // here means the nav option is hidden and a `?tab=comparison` deep link never
                // mounts the tab or calls the endpoint.
                comparisonAllowed:
                    hasSurface("comparison_tab", permissions) &&
                    Boolean(storeUser?.companyFeatureFlags?.comparison_enabled),
                loadComparison: useCallback(
                    (comparisonFileId: string, query: TApiComparisonQuery, signal?: AbortSignal) =>
                        api.files.comparison(comparisonFileId, query, signal),
                    [],
                ),
                useLineItemsState: () => lineItemsState as TAsyncState<TReviewLineItem[]>,
                useLineItemsMeta: () => lineItemsMeta,
                useFacets: () => facets,
                // Server-derived record of the current file's background operation
                // (or null if none has been loaded for it yet). `fileId` lets a
                // consumer ignore a stale record from the previously viewed file
                // until this file's own status has been fetched.
                useOperationState: () => fileOperationStatus,
                onRefresh: handleRefresh,
                onManualRefresh: handleManualRefresh,
                // Always the homepage, not the page the file was opened from. A company
                // without the Homepage v2 board is redirected from /home to its landing
                // page by the root layout.
                onBack: useCallback(() => {
                    navigate(routerPaths.home);
                }, [navigate]),
                // The month picker resolved to a different file of the same submission.
                // Same route, so the view stays mounted and refetches in place;
                // `replace` keeps month- and type-hopping out of the history stack.
                // `period` rides along: `fileState` still describes the file being
                // left behind for the length of the switch, so dropping the
                // param here would make the shell's `selectedPeriod` fall back to that
                // file's own month and the picker would flash back to it until the
                // sibling loads. `type` rides along for the same reason: without it the
                // shell would fall back to the outgoing file's own type and the selector
                // would flash back to it until the sibling loads.
                onOpenSiblingFile: useCallback(
                    (
                        siblingFileId: string,
                        params?: { tab?: string; period?: string; type?: string },
                    ) => {
                        const search = new URLSearchParams();
                        if (params?.tab) search.set("tab", params.tab);
                        if (params?.period) search.set("period", params.period);
                        if (params?.type) search.set("type", params.type);
                        const searchString = search.toString();
                        navigate(
                            {
                                pathname: routerPaths.files.detail(siblingFileId),
                                search: searchString ? `?${searchString}` : "",
                            },
                            { replace: true },
                        );
                    },
                    [navigate],
                ),
                useIsViewingAsAdmin: () => isViewingAsAdmin,
                useFinjournalReviewCaps: () => finjournalReviewCaps,
                useTbglReviewCaps: () => tbglReviewCaps,
                useCanAutoBalance: () => canAutoBalance,
                useCanForceReject: () => canForceReject,
                useCanMarkCompleted: () => canMarkCompleted,
                useSupportingFilesState: () => supportingFilesListState,
                fetchSupportingFiles,
                // Financial files in a deal a supporting file may be linked to — fetched
                // lazily per deal, the same lazy-by-dealId shape as wireUpload.ts's
                // useFinancialFiles for the standalone upload page.
                useRelatableFiles: (dealId: string | null) => {
                    const [relatableFiles, setRelatableFiles] = useState<
                        { id: string; name: string }[]
                    >([]);
                    useEffect(() => {
                        if (!dealId) {
                            setRelatableFiles([]);
                            return;
                        }
                        let cancelled = false;
                        api.supportingFiles
                            .relatableFiles(dealId)
                            .then((files) => {
                                if (cancelled) return;
                                setRelatableFiles(files.map((f) => ({ id: f.id, name: f.name })));
                            })
                            .catch(() => {
                                if (!cancelled) setRelatableFiles([]);
                            });
                        return () => {
                            cancelled = true;
                        };
                    }, [dealId]);
                    return relatableFiles;
                },
                onUploadSupportingFile: useCallback(
                    async (input: {
                        dealId: string;
                        period: string;
                        file: File;
                        friendlyName: string;
                        financialFileId: string | null;
                        notes: string | null;
                    }) => {
                        await api.supportingFiles.upload(input.file, {
                            deal_id: input.dealId,
                            friendly_name: input.friendlyName,
                            // The upload endpoint's `period` form field is YYYY-MM
                            // (it appends "-01" itself), but the submission context's
                            // `period` this is sourced from is a full YYYY-MM-DD date —
                            // truncate rather than forward it verbatim.
                            period: input.period.slice(0, 7),
                            financial_file_id: input.financialFileId || undefined,
                            notes: input.notes || undefined,
                        });
                    },
                    [],
                ),
                onUpdateSupportingFile: useCallback(
                    async (
                        fileId: string,
                        input: {
                            friendlyName: string;
                            notes: string | null;
                            financialFileId: string | null;
                            clearFinancialFile: boolean;
                            rowVersion: string | null;
                        },
                    ) => {
                        await api.supportingFiles.update(fileId, {
                            friendly_name: input.friendlyName,
                            notes: input.notes,
                            financial_file_id: input.financialFileId || undefined,
                            clear_financial_file: input.clearFinancialFile,
                            row_version: input.rowVersion ?? undefined,
                        });
                    },
                    [],
                ),
                onDeleteSupportingFile: useCallback(async (fileId: string) => {
                    await api.supportingFiles.delete(fileId);
                }, []),
                onDownloadSupportingFile: useCallback((fileId: string) => {
                    api.supportingFiles.download(fileId).catch((error: unknown) => {
                        console.error("Download failed:", error);
                    });
                }, []),
                useSupportingFileCaps: () => ({
                    canView: hasSurface("supporting_tab", permissions),
                    canUpload: hasSurface("supporting_upload", permissions),
                    canEdit: hasSurface("supporting_edit", permissions),
                    canDelete: hasSurface("supporting_delete", permissions),
                    canDownload: hasSurface("supporting_download", permissions),
                }),
                useCanSplitLines: () => canSplitLines,
                onSetContributors: useCallback(
                    async (ids: string[]) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) return;
                        const updated = await api.files.setContributors(fileId, ids);
                        setFileState((prev) => ({
                            ...prev,
                            data: prev.data
                                ? {
                                      ...prev.data,
                                      contributors: (updated.contributors ?? []).map((c) => ({
                                          id: c.id,
                                          name: c.display_name,
                                      })),
                                      canEditContributors:
                                          updated.can_edit_contributors ??
                                          prev.data.canEditContributors,
                                  }
                                : null,
                        }));
                    },
                    [api.files],
                ),
                // Eligible contributor candidates for the review-page editor, scoped
                // by the current user's role. Only fetched when the editor is shown.
                useContributorCandidates: (dealId: string | null, enabled: boolean) => {
                    const [candidates, setCandidates] = useState<{
                        data: { id: string; label: string }[];
                        loading: boolean;
                    }>({ data: [], loading: false });
                    useEffect(() => {
                        if (!enabled) {
                            setCandidates({ data: [], loading: false });
                            return;
                        }
                        let cancelled = false;
                        setCandidates((s) => ({ ...s, loading: true }));
                        api.finjournals
                            .getContributorCandidates(dealId)
                            .then((rows) => {
                                if (cancelled) return;
                                setCandidates({
                                    data: rows.map((r) => ({ id: r.id, label: r.display_name })),
                                    loading: false,
                                });
                            })
                            .catch(() => {
                                if (!cancelled) setCandidates({ data: [], loading: false });
                            });
                        return () => {
                            cancelled = true;
                        };
                    }, [dealId, enabled]);
                    return candidates;
                },
                onAccessDenied: useCallback(() => {
                    // "/" resolves to whichever landing page this user's
                    // permissions and company flags allow (the board, or the
                    // legacy file list) rather than assuming either exists.
                    navigate("/");
                }, [navigate]),
                onAutoBalance: useCallback(async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) return;
                    try {
                        await runOperation(fileId, () => api.files.autoBalance(fileId));
                        if (!isStillViewing(fileId)) return;
                        await loadFile(fileId);
                        await refetchCurrentPage();
                    } catch (error) {
                        console.error("Auto-balance failed:", error);
                        throw error;
                    }
                }, [runOperation, loadFile, refetchCurrentPage, isStillViewing]),
                onCommitOTB: useCallback(async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) return;
                    try {
                        await api.files.commit(fileId);
                        await loadFile(fileId);
                        await refetchCurrentPage();
                    } catch (error) {
                        console.error("OTB commit failed:", error);
                        throw error;
                    }
                }, [api.files, loadFile, refetchCurrentPage]),
                onGenerateGL: useCallback(async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) return;
                    await runOperation(fileId, () => api.files.generateGL(fileId));
                    // Generation can take minutes; if the user moved on, do not reload
                    // this file and above all do not switch them to its GL lines.
                    if (!isStillViewing(fileId)) return;
                    await loadFile(fileId);
                    await refetchCurrentPage();
                    // Still this file, so any ?period=/?type= in the URL agrees with it
                    // and can be dropped (see the shell's isOtherMonthSelected notes).
                    navigate(
                        {
                            pathname: routerPaths.files.detail(fileId),
                            search: "?tab=generated-gl",
                        },
                        { replace: true },
                    );
                }, [runOperation, loadFile, refetchCurrentPage, navigate, isStillViewing]),
                onManualReject: useCallback(
                    async (reason: string) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) return;
                        await api.files.reject(fileId, reason);
                        setFileState((prev) => ({
                            ...prev,
                            data: prev.data ? { ...prev.data, status: "rejected" } : null,
                        }));
                    },
                    [api.files],
                ),
                onAutoPopulatePropcodes: useCallback(
                    async (propcode: string, lineItemIds: string[]) => {
                        if (!currentFileIdRef.current || lineItemIds.length === 0) {
                            return 0;
                        }

                        const { updated_count } = await api.files.autoPopulatePropcodes(
                            currentFileIdRef.current,
                            {
                                propcode,
                                line_item_ids: lineItemIds,
                            },
                        );

                        const lineItemIdSet = new Set(lineItemIds);
                        setLineItemsState((prev) => ({
                            ...prev,
                            data: prev.data
                                ? prev.data.map((item) =>
                                      lineItemIdSet.has(item.id) ? { ...item, propcode } : item,
                                  )
                                : prev.data,
                        }));

                        return updated_count;
                    },
                    [api.files],
                ),
                onAutoPopulatePropcodesByFilter: useCallback(
                    async (
                        filterQuery: TApiLineItemQuery,
                        propcode: string,
                        field: TAutoPopulateField,
                    ) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) return 0;
                        const resp = await api.files.autoPopulatePropcodesByFilter(
                            fileId,
                            { ...filterQuery, exclude_gl_generated: true },
                            { propcode, field },
                        );
                        await refetchCurrentPage();
                        await loadFacets(fileId, undefined, { force: true });
                        return resp.updated_count;
                    },
                    [api.files, refetchCurrentPage, loadFacets],
                ),
                onRemoveGeneratedLines: useCallback(async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) {
                        throw new Error("No file selected");
                    }
                    const final = await runOperation(fileId, () =>
                        api.files.removeGeneratedLines(fileId),
                    );
                    // Reload only what the user is still looking at; the outcome below
                    // is still returned either way so the caller can report it.
                    if (isStillViewing(fileId)) {
                        await loadFile(fileId);
                        await refetchCurrentPage();
                    }
                    // remove_generated_lines is the one operation whose result kept
                    // `message` (it carries file-type-dependent wording); the others
                    // dropped it as derivable boilerplate.
                    return {
                        message:
                            resultString(final.result, "message") ??
                            "Generated lines removed successfully.",
                    };
                }, [runOperation, loadFile, refetchCurrentPage, isStillViewing]),
                onRunAdjustment: useCallback(async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) {
                        throw new Error("No file selected");
                    }
                    const final = await runOperation(fileId, () => api.files.runAdjustment(fileId));
                    if (isStillViewing(fileId)) {
                        await loadFile(fileId);
                        await refetchCurrentPage();
                    }
                    // run_adjustment's result deliberately dropped `message` (it was
                    // derivable boilerplate nothing asserted on) — the view builds
                    // its own success text from the count below.
                    return {
                        adjustments_created: resultNumber(final.result, "adjustments_created"),
                    };
                }, [runOperation, loadFile, refetchCurrentPage, isStillViewing]),
                onGenerateFinJournal: useCallback(async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) return;
                    await api.files.generateFinJournal(fileId);
                    await loadFile(fileId);
                    // The ETL tab is gated on the context's etl_artifacts, not on the
                    // file, so reloading only the file leaves the tab greyed out until
                    // the page is remounted.
                    await loadSubmissionContext(fileId);
                    await refetchCurrentPage();
                }, [api.files, loadFile, loadSubmissionContext, refetchCurrentPage]),
                onGenerateDataPlatform: useCallback(async () => {
                    const fileId = currentFileIdRef.current;
                    if (!fileId) return;
                    await api.files.generateDataPlatform(fileId);
                    await loadFile(fileId);
                    await loadSubmissionContext(fileId);
                    await refetchCurrentPage();
                }, [api.files, loadFile, loadSubmissionContext, refetchCurrentPage]),
                onDownload: useCallback(
                    async (downloadType: string) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) {
                            console.error("No file selected for download");
                            return;
                        }

                        try {
                            const validTypes = [
                                "original",
                                "processed",
                                "errors",
                                "finjournal",
                                "data-platform",
                            ] as const;
                            type TDownloadType = (typeof validTypes)[number];
                            if (!validTypes.includes(downloadType as TDownloadType)) {
                                console.error(`Invalid download type: ${downloadType}`);
                                return;
                            }
                            await api.files.downloadFile(fileId, downloadType as TDownloadType);
                        } catch (error) {
                            console.error("Download failed:", error);
                            throw error;
                        }
                    },
                    [api.files],
                ),
                onDownloadGeneratedExport: useCallback(
                    async (kind: "finjournal" | "data_platform", sourceFileId: string) => {
                        // The artifact's source file, not the file on screen: a TB_AND_GL
                        // deal's export hangs off the GL, so downloading it while viewing
                        // the TB would otherwise fail the backend's availability check.
                        if (!sourceFileId) {
                            console.error("No source file for the requested export");
                            return;
                        }

                        try {
                            await api.files.downloadGeneratedExport(sourceFileId, kind);
                        } catch (error) {
                            console.error("Download failed:", error);
                            throw error;
                        }
                    },
                    [api.files],
                ),
                onChangeStatus: useCallback(
                    async (newStatus: string) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) return;
                        // Completing and reopening change what every other role may do,
                        // so reload the file rather than patching the status locally.
                        if (newStatus === "completed") {
                            await api.files.markCompleted(fileId);
                            await loadFile(fileId);
                            return;
                        }
                        if (currentStatusRef.current === "completed") {
                            await api.files.reopen(fileId);
                            await loadFile(fileId);
                            return;
                        }
                        if (newStatus === "under_review") {
                            await api.files.revertToReview(fileId);
                        } else {
                            await api.files.completeReview(fileId);
                        }
                        setFileState((prev) => ({
                            ...prev,
                            data: prev.data ? { ...prev.data, status: newStatus } : null,
                        }));
                    },
                    [api.files, loadFile],
                ),
                onEditLineItem: useCallback(
                    async (lineItem: TReviewLineItem, updates: Partial<TReviewLineItem>) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) {
                            throw new Error("No file selected");
                        }

                        const apiUpdates: Record<string, unknown> = {
                            mapped_property_code: updates.propcode ?? null,
                            // Asset and investment are optional: a cleared field is sent as
                            // null (which unmaps the line), never as an empty string.
                            mapped_asset_code: updates.mappedAsset || null,
                            mapped_investment_code: updates.mappedInvestment || null,
                            mapped_account_code: updates.breAccountNumber ?? null,
                            mapped_account_name: updates.breAccountName ?? null,
                            description: updates.description ?? null,
                        };

                        // Amount is editable on split rows only, and only then is it
                        // sent — the backend derives debit/credit from it so the file's
                        // totals stay consistent. Omitting the key otherwise keeps the
                        // request identical to before for ordinary rows.
                        if (lineItem.isSplitChild && updates.amount !== undefined) {
                            apiUpdates["amount"] = normalizeAmountInput(updates.amount);
                        }

                        const response = await api.files.updateLineItem(
                            fileId,
                            lineItem.id,
                            apiUpdates,
                        );

                        // The endpoint now returns the whole file-detail payload inline
                        // (FIN-561), so a single-cell edit is one round trip instead of
                        // this PUT followed by a POST /revalidate and a GET /files/{id}.
                        // dealFileType/finalFileDestination cannot change from a line-item
                        // edit, so they carry over from the currently-loaded file rather
                        // than re-fetching the deal.
                        if (response.file) {
                            const apiFile = response.file;
                            currentStatusRef.current = apiFile.status;
                            setFileState((prev) => ({
                                status: "fulfilled",
                                data: mapApiFileToReviewDetails(
                                    apiFile,
                                    prev.data?.dealFileType ?? null,
                                    prev.data?.finalFileDestination ?? null,
                                ),
                                error: null,
                            }));
                        }
                        await refetchCurrentPage();
                        // A newly entered propcode/account value must become a filter
                        // option, so refresh the facets after the edit lands.
                        await loadFacets(fileId, undefined, { force: true });
                    },
                    [refetchCurrentPage, loadFacets],
                ),
                // Add `count` split rows to a line — creating the split, or appending
                // to one that already exists.
                onSplitLineItem: useCallback(
                    async (lineItem: TReviewLineItem, count: number) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) {
                            throw new Error("No file selected");
                        }
                        const response = await api.files.splitLineItem(fileId, lineItem.id, {
                            count,
                        });
                        // Consume the inline file payload (FIN-561) instead of
                        // paying afterSplitChange's extra GET /files/{id}.
                        if (response.file) {
                            applyFileFromResponse(response.file);
                        }
                        await refetchCurrentPage();
                    },
                    [api.files, applyFileFromResponse, refetchCurrentPage],
                ),
                // Reverse a split: remove every split row, leaving the original as it was.
                onReverseSplit: useCallback(
                    async (lineItem: TReviewLineItem) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) {
                            throw new Error("No file selected");
                        }
                        const response = await api.files.reverseSplit(fileId, lineItem.id);
                        // Consume the inline file payload (FIN-561) instead of
                        // paying afterSplitChange's extra GET /files/{id}.
                        if (response.file) {
                            applyFileFromResponse(response.file);
                        }
                        await refetchCurrentPage();
                    },
                    [api.files, applyFileFromResponse, refetchCurrentPage],
                ),
                onDeleteSplitRow: useCallback(
                    async (lineItem: TReviewLineItem) => {
                        const fileId = currentFileIdRef.current;
                        if (!fileId) {
                            throw new Error("No file selected");
                        }
                        await api.files.deleteLineItem(fileId, lineItem.id);
                        await afterSplitChange(fileId);
                    },
                    [api.files, afterSplitChange],
                ),
                searchMasterPropertyCodes: useCallback(
                    // Server-side search across the company's master property codes of one
                    // kind (matches code OR name). Lazy + paginated so it scales past the
                    // client page cap — the entity, asset and investment dropdowns must
                    // each surface every master code of their kind, not just the first page.
                    (query: string, kind: TPropertyCodeKind) =>
                        searchMasterPropertyCodes(api.propertyCodes, query, kind),
                    [api.propertyCodes],
                ),
                searchYardiAccountsByCode: useCallback(
                    async (query: string) => {
                        // All files resolve accounts from the company-wide master Chart
                        // of Accounts so the full master data is selectable regardless of
                        // deal/business-partner mapping scope.
                        const trimmed = query.trim();
                        const response = await api.chartOfAccounts.list({
                            number: trimmed || undefined,
                            page_size: 200,
                            active_only: true,
                            is_prohibited: false,
                        });
                        return response.items.map((a) => ({
                            value: a.number,
                            label: a.number,
                            code: a.number,
                            name: a.short_name,
                        }));
                    },
                    [api.chartOfAccounts],
                ),
                searchYardiAccountsByName: useCallback(
                    async (query: string) => {
                        // All files resolve accounts from the company-wide master Chart
                        // of Accounts so the full master data is selectable regardless of
                        // deal/business-partner mapping scope.
                        const trimmed = query.trim();
                        const response = await api.chartOfAccounts.list({
                            name: trimmed || undefined,
                            page_size: 200,
                            active_only: true,
                            is_prohibited: false,
                        });
                        return response.items.map((a) => ({
                            value: a.short_name,
                            label: a.short_name,
                            code: a.number,
                            name: a.short_name,
                        }));
                    },
                    [api.chartOfAccounts],
                ),
            };
        },
    });

    return { ViewFileReview };
};
